import React, { useState, useEffect, useCallback } from 'react';
import { Clock, LogIn, LogOut, Briefcase, ArrowLeft, Loader2, Coffee, PlayCircle, AlertCircle } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

/**
 * Compact time-tracking widget for agents/admins.
 * Shows current state + contextual action buttons +
 * live metrics (gross presence / net worked / break / overtime).
 */
export const TimeTrackingWidget = () => {
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ state: 'off', entries: [], enabled: false });
  const [now, setNow] = useState(new Date());

  const fetchStatus = useCallback(async () => {
    if (!isAuthenticated || !user) return;
    if (!['agent', 'admin', 'super_admin'].includes(user.role)) return;
    try {
      const res = await api.get('/time-tracking/my-status');
      setStatus(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Tick every second so live durations stay accurate
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const handleAction = async (action) => {
    setSubmitting(true);
    try {
      const res = await api.post(`/time-tracking/${action}`, {});
      setStatus({ state: res.data.state, entries: res.data.entries, enabled: true });
      const labels = {
        'clock-in': 'Arrivee pointee',
        'mission/depart': 'Depart en mission enregistre',
        'mission/return': 'Retour de mission enregistre',
        'break/start': 'Pause demarree. Bonne pause !',
        'break/end': 'Reprise du travail',
        'clock-out': 'Depart pointe. Bonne journee !',
      };
      toast.success(labels[action] || 'Enregistre');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  if (!user || !['agent', 'admin', 'super_admin'].includes(user.role)) return null;
  if (loading) return null;

  const { state, entries } = status;
  const clockIn = entries.find((e) => e.entry_type === 'clock_in');
  const clockOut = entries.find((e) => e.entry_type === 'clock_out');
  const arrivedAt = clockIn ? new Date(clockIn.timestamp) : null;
  const leftAt = clockOut ? new Date(clockOut.timestamp) : null;

  // ----- Live computation: presence / break / mission / net / overtime (minutes) -----
  let presenceMin = 0;
  let breakMin = 0;
  let missionMin = 0;
  let clockInTs = null;
  let breakStartTs = null;
  let missionStartTs = null;
  for (const e of entries) {
    const ts = new Date(e.timestamp);
    switch (e.entry_type) {
      case 'clock_in':
        clockInTs = ts;
        break;
      case 'clock_out':
        if (clockInTs) {
          presenceMin += (ts - clockInTs) / 60000;
          clockInTs = null;
        }
        break;
      case 'break_start':
        breakStartTs = ts;
        break;
      case 'break_end':
        if (breakStartTs) {
          breakMin += (ts - breakStartTs) / 60000;
          breakStartTs = null;
        }
        break;
      case 'mission_depart':
        missionStartTs = ts;
        break;
      case 'mission_return':
        if (missionStartTs) {
          missionMin += (ts - missionStartTs) / 60000;
          missionStartTs = null;
        }
        break;
      default:
        break;
    }
  }
  // Add open-ended intervals up to "now"
  if (clockInTs) presenceMin += (now - clockInTs) / 60000;
  if (breakStartTs) breakMin += (now - breakStartTs) / 60000;
  if (missionStartTs) missionMin += (now - missionStartTs) / 60000;

  const netMin = Math.max(0, presenceMin - breakMin);
  const STD_MIN = 8 * 60;
  const overtimeMin = Math.max(0, netMin - STD_MIN);

  const fmtMin = (m) => {
    const v = Math.max(0, Math.floor(m || 0));
    return `${Math.floor(v / 60)}h${String(v % 60).padStart(2, '0')}`;
  };

  const stateConfig = {
    off: { label: 'Hors service', color: 'bg-gray-100 text-gray-700', dot: 'bg-gray-400' },
    at_office: { label: 'Au bureau', color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
    on_mission: { label: 'En mission', color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
    on_break: { label: 'En pause', color: 'bg-indigo-100 text-indigo-700', dot: 'bg-indigo-500' },
  };
  const cfg = stateConfig[state] || stateConfig.off;

  const timeStr = now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const dateStr = now.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const showMetrics = !!clockIn;

  return (
    <Card className="border-l-4 border-l-[#D4A745] shadow-sm" data-testid="time-tracking-widget">
      <CardContent className="p-4">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:justify-between">
          {/* Clock + status */}
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-12 h-12 rounded-xl bg-[#1B3A4B] flex items-center justify-center flex-shrink-0">
              <Clock className="w-6 h-6 text-[#D4A745]" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono font-bold text-xl text-[#1B3A4B] tracking-tight" data-testid="tt-clock">{timeStr}</span>
                <Badge className={`${cfg.color} gap-1.5 border-0`} data-testid="tt-state-badge">
                  <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot} ${state !== 'off' ? 'animate-pulse' : ''}`} />
                  {cfg.label}
                </Badge>
              </div>
              <p className="text-xs text-gray-500 capitalize mt-0.5">{dateStr}</p>
              {(arrivedAt || leftAt) && (
                <p className="text-xs text-gray-500 mt-1">
                  {arrivedAt && <>Arrive a <b>{arrivedAt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</b></>}
                  {arrivedAt && leftAt && ' • '}
                  {leftAt && <>Parti a <b>{leftAt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</b></>}
                </p>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-2">
            {state === 'off' && !clockOut && (
              <Button
                onClick={() => handleAction('clock-in')}
                disabled={submitting}
                className="bg-green-600 hover:bg-green-700"
                data-testid="clock-in-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LogIn className="w-4 h-4 mr-2" />}
                Pointer arrivee
              </Button>
            )}

            {state === 'at_office' && (
              <>
                <Button
                  onClick={() => handleAction('break/start')}
                  disabled={submitting}
                  variant="outline"
                  className="border-indigo-500 text-indigo-700 hover:bg-indigo-50"
                  data-testid="break-start-btn"
                >
                  <Coffee className="w-4 h-4 mr-2" />
                  Prendre une pause
                </Button>
                <Button
                  onClick={() => handleAction('mission/depart')}
                  disabled={submitting}
                  variant="outline"
                  className="border-amber-500 text-amber-700 hover:bg-amber-50"
                  data-testid="mission-depart-btn"
                >
                  <Briefcase className="w-4 h-4 mr-2" />
                  Depart mission
                </Button>
                <Button
                  onClick={() => handleAction('clock-out')}
                  disabled={submitting}
                  className="bg-[#1B3A4B] hover:bg-[#142D3A]"
                  data-testid="clock-out-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Pointer depart
                </Button>
              </>
            )}

            {state === 'on_break' && (
              <Button
                onClick={() => handleAction('break/end')}
                disabled={submitting}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="break-end-btn"
              >
                <PlayCircle className="w-4 h-4 mr-2" />
                Reprendre le travail
              </Button>
            )}

            {state === 'on_mission' && (
              <>
                <Button
                  onClick={() => handleAction('mission/return')}
                  disabled={submitting}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="mission-return-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Retour mission
                </Button>
                <Button
                  onClick={() => handleAction('clock-out')}
                  disabled={submitting}
                  variant="outline"
                  className="border-[#1B3A4B] text-[#1B3A4B]"
                  data-testid="clock-out-btn-mission"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Pointer depart
                </Button>
              </>
            )}

            {clockOut && state === 'off' && (
              <div className="text-sm text-gray-500 italic">
                Journee terminee. Rendez-vous demain !
              </div>
            )}
          </div>
        </div>

        {/* Live metrics row (presence / net / break / overtime) */}
        {showMetrics && (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="tt-metrics">
            <div className="bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
              <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Presence</p>
              <p className="text-base font-bold text-[#1B3A4B] tabular-nums" data-testid="tt-presence">{fmtMin(presenceMin)}</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
              <p className="text-[10px] uppercase tracking-wider text-emerald-600 font-semibold">Temps net</p>
              <p className="text-base font-bold text-emerald-700 tabular-nums" data-testid="tt-net">{fmtMin(netMin)}</p>
            </div>
            <div className="bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2">
              <p className="text-[10px] uppercase tracking-wider text-indigo-600 font-semibold">Pause</p>
              <p className="text-base font-bold text-indigo-700 tabular-nums" data-testid="tt-break">{fmtMin(breakMin)}</p>
            </div>
            <div className={`border rounded-lg px-3 py-2 ${overtimeMin > 0 ? 'bg-[#D4A745]/10 border-[#D4A745]/30' : 'bg-gray-50 border-gray-100'}`}>
              <p className={`text-[10px] uppercase tracking-wider font-semibold ${overtimeMin > 0 ? 'text-[#9C7710]' : 'text-gray-500'}`}>
                Heures sup
              </p>
              <p className={`text-base font-bold tabular-nums flex items-center gap-1 ${overtimeMin > 0 ? 'text-[#9C7710]' : 'text-gray-400'}`} data-testid="tt-overtime">
                {overtimeMin > 0 && <AlertCircle className="w-3.5 h-3.5" />}
                {fmtMin(overtimeMin)}
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
